import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardContent,
  Checkbox,
  Divider,
  Paper,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material';
import { formatRequiresDigitalSignature } from 'components/@home/@documents/SentDocuments/edit-steps/editTemplateFlow';
import { SendDrawerContext } from 'components/@home/@documents/SentDocuments/SendDrawerContext';
import extractionFileMatchesForSend from 'components/@home/@documents/SentDocuments/extractionFileMatch';
import SendMessageEmailPreview from 'components/@home/@documents/SentDocuments/steps/SendMessageEmailPreview';
import SendMessagePhonePreview from 'components/@home/@documents/SentDocuments/steps/SendMessagePhonePreview';
import { getFileName } from 'components/@home/drawers/HrBotDrawer/pdf-utils';
import LinearProgressWithLabel from 'components/controls/LinearProgressWithLabel';
import CheckCircleIcon from 'mdi-react/CheckCircleIcon';
import FilePdfBoxIcon from 'mdi-react/FilePdfBoxIcon';
import moment from 'moment';
import { array, bool, func, node, number, object, oneOf, string } from 'prop-types';
import React, { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { I18n } from 'utils/i18n';

const SummarySection = ({
  title,
  onEdit = null,
  editLabel = '',
  editDisabled = false,
  children,
}) => {
  return (
    <Card variant="outlined">
      <CardHeader
        title={
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        }
        action={
          onEdit ? (
            <Button size="small" color="primary" onClick={onEdit} disabled={editDisabled}>
              {editLabel}
            </Button>
          ) : null
        }
        sx={theme => ({
          borderBottom: `1px solid ${theme.palette.divider}`,
          p: 1,
          px: 2,
          minHeight: 48,
          '& .MuiCardHeader-action': {
            mt: 0,
            mr: 0,
            alignSelf: 'center',
          },
        })}
        disableTypography
      />
      <CardContent>{children}</CardContent>
    </Card>
  );
};

SummarySection.propTypes = {
  title: string.isRequired,
  onEdit: func,
  editLabel: string,
  editDisabled: bool,
  children: node.isRequired,
};

const StepSend = ({
  setError,
  setNextEnabled,
  userItems,
  uploadedPct,
  messageSubject = '',
  message,
  signatureCardTitle = '',
  isRoutedSend = false,
  files = [],
  format: formatProp = null,
  emailRecipients = [],
  sendChannel = 'internal',
  internalPickerCount = 0,
  saveAsTemplate = false,
  setSaveAsTemplate = null,
  saveAsTemplateName = '',
  setSaveAsTemplateName = null,
  sendPhase,
  deliveryOptions = {},
  sequential = false,
  signingOrderRecipientCount = 0,
  onEditStep = null,
}) => {
  const { file: formatCtx } = useContext(SendDrawerContext);
  const format = formatProp || formatCtx;
  const filesSafe = files || [];

  useEffect(() => {
    setNextEnabled(
      sendChannel === 'external' ||
        isRoutedSend ||
        Boolean(signatureCardTitle?.trim()) ||
        Boolean(message?.trim()),
    );
    setError(null);
  }, [message, signatureCardTitle, isRoutedSend, sendChannel, setError, setNextEnabled]);

  const bulkMessaging = useSelector(state => state.bulkMessaging.bulkMessaging);
  if (!format) {
    return null;
  }
  const sendByField = format.sendBy || 'code';
  const fileOrig = filesSafe.find(f => !f.error && extractionFileMatchesForSend(f, sendByField));
  const file = { ...fileOrig };
  const numSend = filesSafe.filter(f => extractionFileMatchesForSend(f, sendByField)).length;
  const numNotFound = filesSafe.filter(
    f => !f.error && !extractionFileMatchesForSend(f, sendByField),
  ).length;
  const extractionRowsTotal = filesSafe.filter(f => !f.error).length;
  const name = getFileName({ ...file, ...userItems }, format);

  const extSigners = (emailRecipients || []).filter(r => r.email?.trim());
  const showOmmnioSummary = sendChannel === 'internal' || sendChannel === 'mixed';
  const showEmailSummary = sendChannel === 'external' || sendChannel === 'mixed';

  const previewName = file?.file?.size ? name : format.name;
  const previewSize = file?.file?.size || 0;
  let ommnioSummary = I18n.t('HrBot.will be sent', { count: numSend });
  if (internalPickerCount > 0) {
    ommnioSummary = I18n.t('HrBot.SendStep.pickerWillDm', { count: internalPickerCount });
  } else if (format.documentSource === 'fixed' && !filesSafe.length) {
    ommnioSummary = I18n.t('HrBot.SendStep.fixedDocumentWillSend');
  }
  const showEphemeralSave = !format?._id;

  const modeLabel =
    format.documentSource === 'fixed'
      ? I18n.t('SignTracking.Modes.fixed')
      : I18n.t('SignTracking.Modes.perSend');

  const isSummary = sendPhase === 'summary';
  const fieldCount = (format.fields || []).length;
  const scheduleSigningDelivery = formatRequiresDigitalSignature(format);
  let reviewMessageLabelKey = 'HrBot.SendStep.reviewMessage';
  if (sendChannel === 'external') {
    reviewMessageLabelKey = 'HrBot.SendStep.reviewEmailMessage';
  } else if (scheduleSigningDelivery && sendChannel === 'internal') {
    reviewMessageLabelKey = 'HrBot.SendStep.reviewMessageToEmployee';
  }
  const signingModeLabel = scheduleSigningDelivery
    ? I18n.t(`HrBot.EditTemplate.SigningMode.${format.signingMode || 'onePerMember'}`)
    : null;

  let expiresLabel = I18n.t('HrBot.SendStep.deliveryNoExpiration');
  if (
    scheduleSigningDelivery &&
    deliveryOptions?.expiresPreset !== 'never' &&
    deliveryOptions?.expiresAt &&
    String(deliveryOptions.expiresAt).trim()
  ) {
    expiresLabel = moment(deliveryOptions.expiresAt).format('L');
  }

  const remindersOn = scheduleSigningDelivery && !!deliveryOptions?.reminderIntervalDays;
  const remindersValue = remindersOn
    ? I18n.t('HrBot.SendStep.deliveryRemindersOnShort', {
        days: deliveryOptions.reminderIntervalDays,
      })
    : I18n.t('HrBot.SendStep.deliveryRemindersOffShort');

  const showSigningOrderSummary =
    scheduleSigningDelivery &&
    (format.signingMode || 'onePerMember') === 'oneForAll' &&
    signingOrderRecipientCount >= 2;
  let signingOrderSummaryLabel = null;
  if (showSigningOrderSummary) {
    signingOrderSummaryLabel = sequential
      ? I18n.t('HrBot.SendStep.signingOrder.sequential')
      : I18n.t('HrBot.SendStep.signingOrder.parallel');
  }

  const reportTo = format.sendReportTo;
  const hasAuditTarget =
    reportTo?.type &&
    reportTo.value !== undefined &&
    reportTo.value !== null &&
    String(reportTo.value) !== '-1';
  let auditLabel = I18n.t('HrBot.SendStep.auditOff');
  if (hasAuditTarget) {
    const kind =
      reportTo.type === 'group'
        ? I18n.t('HrBot.SendStep.auditToGroup')
        : I18n.t('HrBot.SendStep.auditToEmployee');
    auditLabel = reportTo.label ? `${kind}: ${reportTo.label}` : kind;
  }

  const recipientDetailLines = [];
  if (showEmailSummary) {
    recipientDetailLines.push({
      id: 'email',
      text: I18n.t('HrBot.SendStep.externalWillEmail', { count: extSigners.length }),
    });
  }
  if (showOmmnioSummary) {
    if (internalPickerCount > 0) {
      recipientDetailLines.push({
        id: 'picker',
        text: I18n.t('HrBot.SendStep.recipientPickerTotal', { count: internalPickerCount }),
      });
    } else if (extractionRowsTotal > 0) {
      recipientDetailLines.push({
        id: 'extraction-matched',
        text: I18n.t('HrBot.SendStep.recipientExtractionMatched', {
          total: extractionRowsTotal,
          matched: numSend,
        }),
      });
      if (numNotFound > 0) {
        recipientDetailLines.push({
          id: 'extraction-unmatched',
          text: I18n.t('HrBot.SendStep.recipientExtractionUnmatched', { count: numNotFound }),
        });
      }
    } else {
      recipientDetailLines.push({ id: 'ommnio-default', text: ommnioSummary });
    }
  }

  const documentModeDescription = scheduleSigningDelivery
    ? I18n.t('HrBot.SendStep.documentModeSignature', { signingMode: signingModeLabel })
    : I18n.t('HrBot.SendStep.documentModeDistribution');

  const editLabel = I18n.t('HrBot.SendStep.editAction');
  const handleEditRecipients = onEditStep ? () => onEditStep('recipients') : null;
  const handleEditDocument = onEditStep ? () => onEditStep('document') : null;
  const handleEditDelivery = onEditStep ? () => onEditStep('delivery') : null;

  const showDocumentTile = !!file?.file || format?.documentSource === 'fixed';
  const documentRows = [
    {
      id: 'mode',
      label: I18n.t('HrBot.SendStep.reviewMode'),
      value: modeLabel,
    },
    {
      id: 'type',
      label: I18n.t('HrBot.SendStep.documentTypeLabel'),
      value: documentModeDescription,
    },
    {
      id: 'fields',
      label: I18n.t('HrBot.SendStep.documentFieldsLabel'),
      value: String(fieldCount),
    },
  ];
  const deliveryRows = [
    ...(scheduleSigningDelivery
      ? [
          {
            id: 'expiration',
            label: I18n.t('HrBot.SendStep.deliveryExpires'),
            value: expiresLabel,
          },
          {
            id: 'reminders',
            label: I18n.t('HrBot.SendStep.deliveryRemindersLabel'),
            value: remindersValue,
          },
        ]
      : []),
    ...(signingOrderSummaryLabel
      ? [
          {
            id: 'signing-order',
            label: I18n.t('HrBot.SendStep.signingOrder.title'),
            value: signingOrderSummaryLabel,
          },
        ]
      : []),
    {
      id: 'audit',
      label: I18n.t('HrBot.SendStep.deliveryAudit'),
      value: auditLabel,
    },
  ];

  return (
    <Box sx={{ flexGrow: 1, width: '100%', display: 'flex', flexDirection: 'column' }}>
      {isSummary && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, pt: 3 }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <SummarySection
              title={I18n.t('HrBot.SendStep.sectionRecipients')}
              onEdit={handleEditRecipients}
              editLabel={editLabel}
            >
              {recipientDetailLines.map(({ id, text }) => (
                <Typography key={id} variant="body2" sx={{ mt: 1 }}>
                  {text}
                </Typography>
              ))}
            </SummarySection>

            <SummarySection
              title={I18n.t('HrBot.SendStep.sectionDocument')}
              onEdit={handleEditDocument}
              editLabel={editLabel}
            >
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                {format.name}
              </Typography>
              {!!format._id && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    display: 'block',
                    mt: 0.5,
                  }}
                >
                  {I18n.t('HrBot.SendStep.usingSavedTemplate')}
                </Typography>
              )}
              <Box sx={{ mt: 1.5 }}>
                {documentRows.map(({ id, label, value }) => (
                  <Box
                    key={id}
                    sx={theme => ({
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      py: 1,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      '&:last-of-type': { borderBottom: 0 },
                    })}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right' }}>
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {showDocumentTile && (
                <Box
                  sx={theme => ({
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: theme.spacing(0.5),
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.action.hover,
                    mt: 2,
                  })}
                >
                  <SvgIcon sx={{ flexShrink: 0, fontSize: 32 }} color="error">
                    <FilePdfBoxIcon />
                  </SvgIcon>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                      {previewName}
                    </Typography>
                    {previewSize > 0 ? (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        {(previewSize / 1024).toFixed(1)} KB
                      </Typography>
                    ) : null}
                  </Box>
                </Box>
              )}
            </SummarySection>

            <SummarySection
              title={I18n.t('HrBot.SendStep.sectionDelivery')}
              onEdit={handleEditDelivery}
              editLabel={editLabel}
            >
              {deliveryRows.map(({ id, label, value }) => (
                <Box
                  key={id}
                  sx={theme => ({
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    py: 1,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    '&:last-of-type': { borderBottom: 0 },
                  })}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right' }}>
                    {value}
                  </Typography>
                </Box>
              ))}
            </SummarySection>

            {showEphemeralSave && setSaveAsTemplate && (
              <Paper
                elevation={0}
                sx={theme => ({
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  p: 2,
                  borderRadius: theme.spacing(1.5),
                  backgroundColor:
                    theme.palette.mode === 'dark' ? theme.palette.action.hover : '#f5f7ff',
                  border: `1px solid ${theme.palette.divider}`,
                })}
              >
                <Checkbox
                  checked={!!saveAsTemplate}
                  onChange={e => setSaveAsTemplate(e.target.checked)}
                  sx={{ p: 0, mt: -0.25 }}
                />
                <FolderOutlinedIcon sx={{ fontSize: 22, color: 'text.secondary', mt: 0.25 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {I18n.t('HrBot.SendStep.saveAsTemplateCardTitle')}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      display: 'block',
                    }}
                  >
                    {I18n.t('HrBot.SendStep.saveAsTemplateCardSubtitle')}
                  </Typography>
                  {saveAsTemplate && (
                    <TextField
                      fullWidth
                      size="small"
                      label={I18n.t('HrBot.SendStep.saveAsTemplateNameLabel')}
                      value={saveAsTemplateName}
                      onChange={e => setSaveAsTemplateName(e.target.value)}
                      sx={{ maxWidth: 400, mt: 1.5 }}
                    />
                  )}
                </Box>
              </Paper>
            )}
          </Box>
          <Box
            sx={{
              pl: sendChannel === 'external' ? { xs: 1, md: 3 } : 12,
              pr: sendChannel === 'external' ? { xs: 1, md: 3 } : 12,
              ...(sendChannel === 'external' && { pt: { xs: 1, md: 2 } }),
            }}
          >
            {sendChannel === 'external' ? (
              <SendMessageEmailPreview
                messageSubject={messageSubject}
                message={message}
                files={filesSafe}
                userItems={userItems}
                format={format}
                emailRecipients={emailRecipients}
              />
            ) : (
              <SendMessagePhonePreview
                message={message}
                signatureCardTitle={signatureCardTitle}
                isRoutedSend={isRoutedSend}
                files={filesSafe}
                userItems={userItems}
                format={format}
              />
            )}
          </Box>
        </Box>
      )}

      {!isSummary && (
        <Paper
          elevation={0}
          sx={theme => ({
            p: 2,
            borderRadius: theme.spacing(1),
            backgroundColor: theme.palette.secondary.ultraLight,
            border: `1px solid ${theme.palette.divider}`,
            mt: 2,
          })}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              display: 'block',
            }}
          >
            {I18n.t('HrBot.SendStep.reviewDocument')}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
            {format.name}
          </Typography>
          <Divider sx={{ my: 1.5 }} />
          {sendChannel === 'external' && (
            <>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                }}
              >
                {I18n.t('HrBot.SendStep.reviewMessageSubject')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                {(messageSubject || '').trim() || format.name || '—'}
              </Typography>
              <Divider sx={{ my: 1.5 }} />
            </>
          )}
          {sendChannel === 'internal' && formatRequiresDigitalSignature(format) && (
            <>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                }}
              >
                {I18n.t('HrBot.SendStep.reviewSignatureCardTitle')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                {isRoutedSend
                  ? I18n.t('HrBot.SendStep.signatureCardTitleRoutedHint')
                  : (signatureCardTitle || '').trim() || format.name || '—'}
              </Typography>
              <Divider sx={{ my: 1.5 }} />
            </>
          )}
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              display: 'block',
            }}
          >
            {I18n.t(reviewMessageLabelKey)}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
            {message || '—'}
          </Typography>
        </Paper>
      )}

      {!isSummary && (
        <Box>
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2">{I18n.t('HrBot.Upload progress')}</Typography>
            <LinearProgressWithLabel value={uploadedPct} />
          </Box>
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2">{I18n.t('HrBot.Send progress')}</Typography>
            <LinearProgressWithLabel
              value={
                bulkMessaging
                  ? ((bulkMessaging.sent + bulkMessaging.error) / bulkMessaging.total) * 100
                  : 0
              }
            />
            {bulkMessaging?.error > 0 && (
              <Typography color="error">
                {I18n.t('HrBot.Error sending files', { count: bulkMessaging.error })}
              </Typography>
            )}
          </Box>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2">
              {I18n.t(bulkMessaging ? 'HrBot.You can now close' : 'HrBot.Please do not close')}
            </Typography>
          </Box>
          {bulkMessaging?.status === 'FINISHED' && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Box sx={theme => ({ color: theme.palette.ok, display: 'inline-flex' })}>
                <CheckCircleIcon />
              </Box>
              <Typography variant="body2">
                {I18n.t('HrBot.Finished', { count: bulkMessaging.sent })}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

StepSend.propTypes = {
  setError: func.isRequired,
  setNextEnabled: func.isRequired,
  userItems: object.isRequired,
  uploadedPct: number.isRequired,
  messageSubject: string,
  message: string.isRequired,
  signatureCardTitle: string,
  isRoutedSend: bool,
  files: array,
  format: object,
  emailRecipients: array,
  sendChannel: string,
  internalPickerCount: number,
  saveAsTemplate: bool,
  setSaveAsTemplate: func,
  saveAsTemplateName: string,
  setSaveAsTemplateName: func,
  sendPhase: oneOf(['summary', 'sending']).isRequired,
  deliveryOptions: object,
  sequential: bool,
  signingOrderRecipientCount: number,
  onEditStep: func,
};

export default StepSend;
